import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'formik';
import { IDetectedBarcode, Scanner } from '@yudiel/react-qr-scanner';
import CloseIcon from '@mui/icons-material/Close';
import { Stack, Box, Typography, CircularProgress } from '@mui/material';
import ModalWindow from '@ui-lib/components/ModalWindow';
import IconButton from '@ui-lib/components/IconButton';
import { createSx } from '@ui-lib/utils';
import { useSnackbarContext } from '@ui-lib/components/Snackbar/SnackbarProvider';
import { useConfirmationModal } from '@ui-lib/components/ModalWindow/ConfirmationModal/useConfirmationModal';
import useManualRequest from '@web-shared/hooks/useManualRequest';
import { useModal } from '@web-shared/hooks';
import HttpError from '@web-shared/services/http-service/HttpError';
import { CreateBolFormValues } from '../../const';
import { validateEbolToken } from '../../../../api/index';
import { EBOL_TOKEN_VALIDATION_STATUS } from '../../../../api/enums/ebol-token-validation-status';
import QrScanned from './components/QrScan/QrScanned';
import ScanQrCode from './components/QrScan/ScanQrCode';

const sx = createSx({
  container: {
    width: '100%',
    maxWidth: 500,
    alignSelf: 'center',
    boxSizing: 'border-box',
    '&.loading-container': {
      mt: 2,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }
  }
});

export const QrPreview = connect<Record<string, unknown>, CreateBolFormValues>(({ formik: { values, setFieldValue } }) => {
  const { t } = useTranslation();
  const [open, handleModalStateChange] = useModal();
  const { confirm, confirmationModal } = useConfirmationModal();

  const { showErrorSnackbar } = useSnackbarContext();
  const ebolTokenValidationReq = useManualRequest(validateEbolToken);

  const saveToken = async (data: IDetectedBarcode[]) => {
    handleModalStateChange();
    const rawValue = data[0]?.rawValue ?? '';
    const token = rawValue.split('ebol-manager.over-haul.com/public/ebol/')[1];

    if (!token) {
      setFieldValue('token', '');
      showErrorSnackbar({ message: t('qrCodeError') });
      return;
    }

    try {
      const validationData = await ebolTokenValidationReq.trigger({ token });

      switch (validationData.status) {
        case EBOL_TOKEN_VALIDATION_STATUS.ASSIGNED: {
          const confirmed = await confirm({
            title: t('bolCreatePage_relinkQrCode'),
            subTitle: t('bolCreatePage_relinkQrCodeDescription', { ref: values.reference_number }),
            modalType: 'warning',
            submitBtnLabel: t('yes'),
          });
          if (confirmed) setFieldValue('token', token);
          break;
        }
        case EBOL_TOKEN_VALIDATION_STATUS.INVALID: {
          setFieldValue('token', '');
          showErrorSnackbar({ message: t('errorMessage_qrCodeInvalid') });
          break;
        }
        case EBOL_TOKEN_VALIDATION_STATUS.UNASSIGNED: {
          setFieldValue('token', token);
        }
      };
    } catch (error) {
      if (HttpError.isHttpError(error)) {
        setFieldValue('token', '');
        showErrorSnackbar({ message: error.message });
      }
    }
  };

  const value = values.token;

  if (ebolTokenValidationReq.loading) {
    return (
      <Stack sx={sx.container} spacing={2} className='loading-container'>
        <CircularProgress />
        <Typography variant='body2'>
          {t('infoMessage_qrCodeValidation')}
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      <Stack sx={sx.container} spacing={2}>
        {value ?
          <QrScanned handleModalStateChange={handleModalStateChange} />
          :
          <ScanQrCode handleModalStateChange={handleModalStateChange} />
        }
      </Stack>
      {confirmationModal}
      <ModalWindow
        testID='modal-window'
        open={open}
        onClose={handleModalStateChange}>
        <Scanner
          onScan={saveToken}
          sound={false}
        >
          <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
            <IconButton
              variant='contained'
              title={t('close')}
              onClick={handleModalStateChange}
            >
              <CloseIcon />
            </IconButton>
          </Box>

        </Scanner>
      </ModalWindow>
    </>
  );
});
